import React from 'react';
import { Form, Col, Row } from 'react-bootstrap';
import copy from 'copy-to-clipboard';
import i18n from 'i18n';
import styles from './codePage.module.scss';
import { toast } from 'react-toastify';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { Trans } from 'react-i18next';
import ToggleButtons from 'components/common/ToggleButtons/ToggleButtons';

export type CodeOption = {
  value: string;
  label: string;
  codeSource: () => Promise<string> | string;
};

const SINGLE_CODE_VALUE = 'default';

type CodePageBaseProps = {
  id: string;
  i18nWordingPrefix: string;
};

/**
 * 代碼來源二選一：只有一種代碼時給 `codeSource`；
 * 要讓使用者在多種代碼間切換（例如 JS 追蹤碼 / 圖像追蹤碼）時給 `codeOptions`，會多出一排切換按鈕。
 */
type CodePageProps = CodePageBaseProps &
  (
    | { codeSource: () => Promise<string> | string; codeOptions?: never }
    | { codeOptions: CodeOption[]; codeSource?: never }
  );

type CodePageState = {
  loading: boolean;
  prettyCodes: { [value: string]: string };
  selectedCode: string;
};

export class CodePage extends React.PureComponent<
  CodePageProps,
  CodePageState
> {
  options: CodeOption[];

  constructor(props) {
    super(props);
    this.options =
      props.codeOptions && props.codeOptions.length > 0
        ? props.codeOptions
        : [
            {
              value: SINGLE_CODE_VALUE,
              label: '',
              codeSource: props.codeSource
            }
          ];
    this.state = {
      loading: true,
      prettyCodes: {},
      selectedCode: this.options[0].value
    };
  }

  componentDidMount() {
    this.getCode(this.state.selectedCode);
  }

  async getCode(value: string) {
    const option = this.options.find((option) => option.value === value);
    if (!option) {
      return;
    }
    const code = await option.codeSource();
    const prettyModule = await import('pretty');
    const pretty = prettyModule.default;
    this.setState((state) => ({
      // 載入期間可能已經被切到別的代碼，這裡只寫入快取，loading 交給當下選中的那次載入收尾
      loading: state.selectedCode === value ? false : state.loading,
      prettyCodes: { ...state.prettyCodes, [value]: pretty(code) }
    }));
  }

  changeCodeType = (value: string | number) => {
    const selectedCode = value.toString();
    if (selectedCode === this.state.selectedCode) {
      return;
    }
    const loaded = this.state.prettyCodes[selectedCode] !== undefined;
    this.setState({ selectedCode, loading: !loaded });
    if (!loaded) {
      this.getCode(selectedCode);
    }
  };

  get currentCode(): string {
    return this.state.prettyCodes[this.state.selectedCode] || '';
  }

  copyCode = () => {
    copy(this.currentCode);
    toast.success(i18n.t('codePage.labels.copyCodeSuccess'));
  };

  getPrivacyStatement = () => {
    const openPolicy = () => {
      window.open(
        'https://www.tenmax.io/tw/privacy-policy',
        '_blank',
        'noopener'
      );
    };
    const privacyStatementElements = (
      <Trans
        i18nKey='codePage.labels.privacyStatement'
        components={{ 1: <span onClick={openPolicy} /> }}
      />
    );
    return privacyStatementElements;
  };

  renderCodeTypeToggle() {
    if (this.options.length < 2) {
      return null;
    }
    return (
      <Form.Group as={Row}>
        <Col sm={2}>
          <Form.Label column>
            {i18n.t(`${this.props.i18nWordingPrefix}.codeType`)}
          </Form.Label>
        </Col>
        <Col sm={10}>
          <ToggleButtons
            className={styles.codeType}
            name='codeType'
            value={this.state.selectedCode}
            options={this.options.map(({ value, label }) => ({
              value,
              label
            }))}
            onChange={this.changeCodeType}
          />
        </Col>
      </Form.Group>
    );
  }

  render() {
    return (
      <div className={styles.codePage}>
        {this.state.loading && <LoadingIndicator />}
        <Form.Group as={Row}>
          <Col sm={2}>
            <Form.Label column>
              {i18n.t(`${this.props.i18nWordingPrefix}.id`)}
            </Form.Label>
          </Col>
          <Col sm={10}>
            <Form.Label column>{this.props.id}</Form.Label>
          </Col>
        </Form.Group>
        {this.renderCodeTypeToggle()}
        <Form.Group className={styles.code} as={Row}>
          <Col sm={2}>
            <Form.Label column>
              {i18n.t(`${this.props.i18nWordingPrefix}.code`)}
            </Form.Label>
          </Col>
          <Col sm={10}>
            <textarea
              value={this.currentCode}
              onClick={this.copyCode}
              readOnly
            />
            <span className={styles.hint}>
              {i18n.t(`${this.props.i18nWordingPrefix}.codeHint`)}
            </span>
          </Col>
        </Form.Group>
        <Form.Group className={styles.privacy} as={Row}>
          <Col sm={2}>
            <Form.Label column>{i18n.t('codePage.labels.privacy')}</Form.Label>
          </Col>
          <Col sm={10}>
            <Form.Label column>{this.getPrivacyStatement()}</Form.Label>
          </Col>
        </Form.Group>
      </div>
    );
  }
}
